import { calendarApi } from '@infrastructure/api';
import { pricingService } from '../services/pricing.service';
import DayOfWeek from '@domains/shared/models/day-of-week.value-object';
import Time from '@domains/shared/models/time.value-object';
function firstNumber(source, ...fieldNames) {
    for (const field of fieldNames) {
        const value = source[field];
        if (value === null || value === undefined || value === '')
            continue;
        const parsed = Number(value);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return null;
}
function numberOrDefault(source, fieldNames, fallback = 0) {
    return firstNumber(source, ...fieldNames) ?? fallback;
}
function nullableNumber(source, ...fieldNames) {
    return firstNumber(source, ...fieldNames);
}
function calculateTotalPrice(source, netPrice) {
    return pricingService.round(pricingService.calculateTotalPrice({
        netPrice,
        commissionAmount: numberOrDefault(source, ['commission_amount', 'commissionAmount']),
        commissionType: (source.commission_type || source.commissionType || 'PERCENTAGE'),
        taxRate: nullableNumber(source, 'tax_rate', 'taxRate') ?? undefined,
    }));
}
function transformApiItem(item) {
    if (!item)
        return null;
    const id = item.id || item.availability_id || item.override_id || undefined;
    const itemRecord = item;
    const startingDate = String(item.startingDate || item.starting_date || '');
    const endingDate = String(item.endingDate || item.ending_date || '');
    const startingSchedule = String(item.startingSchedule || item.start_time || '');
    const endingSchedule = String(item.endingSchedule || item.end_time || '');
    const netPrice = numberOrDefault(itemRecord, ['netPrice', 'net_price', 'basePrice', 'base_price']);
    const totalPrice = firstNumber(itemRecord, 'totalPrice', 'total_price') ?? calculateTotalPrice(itemRecord, netPrice);
    const baseTransform = {
        id,
        isExisting: true,
        isNew: false,
        isModified: false,
        net_price: netPrice,
        price_type: (item.priceType || item.price_type || 'PERSON'),
        total_price: totalPrice,
        commission_amount: numberOrDefault(itemRecord, ['commissionAmount', 'commission_amount']),
        commission_type: (item.commissionType || item.commission_type || 'PERCENTAGE'),
        tax_rate: nullableNumber(itemRecord, 'taxRate', 'tax_rate') ?? 0.13,
        standard_max_participants: numberOrDefault(itemRecord, ['standardMaxParticipants', 'standard_max_participants', 'maxParticipants', 'max_participants'], 1),
        overcapacity_max_participants: nullableNumber(itemRecord, 'overcapacityMaxParticipants', 'overcapacity_max_participants'),
        overflow_price: nullableNumber(itemRecord, 'overflowPrice', 'overflow_price'),
        allow_reservations: item.allowReservations !== false && item.allow_reservations !== false,
        start_time: Time.parse(startingSchedule),
        end_time: Time.parse(endingSchedule),
    };
    if (item.type === 'availability') {
        const daysOfWeek = parseDaysOfWeek(startingDate, endingDate);
        const dayOfWeek = item.day_of_week && DayOfWeek.isValidKey(item.day_of_week) ? [item.day_of_week] : [];
        return {
            type: 'availability',
            ...baseTransform,
            schedule: [{
                    ...baseTransform,
                    days_of_week: daysOfWeek.length > 0 ? daysOfWeek : dayOfWeek,
                }],
        };
    }
    if (item.type === 'override') {
        return {
            type: 'override',
            ...baseTransform,
            overrideStartingFrom: parseDate(startingDate) || '',
            overrideUntil: parseDate(endingDate) || '',
        };
    }
    return null;
}
function parseDaysOfWeek(startingDate, endingDate) {
    if (!startingDate || !endingDate)
        return [];
    if (DayOfWeek.isValidKey(startingDate) && DayOfWeek.isValidKey(endingDate)) {
        return DayOfWeek.getRange(startingDate, endingDate);
    }
    return [];
}
function parseDate(dateString) {
    if (!dateString)
        return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }
    const match = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
        const [, day, month, year] = match;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
}
export const calendarRepository = {
    async getByServiceId(serviceId) {
        try {
            const response = await calendarApi.getServiceCalendar(serviceId);
            if (response.status !== 200 || !response.data?.availability?.length) {
                return { calendarId: null, items: [] };
            }
            const rawItems = response.data.availability;
            const items = rawItems
                .map(transformApiItem)
                .filter((item) => item !== null);
            const responseData = response.data;
            const firstRawItem = rawItems[0];
            const calendarId = responseData.calendar?.id
                || responseData.calendar_id
                || responseData.calendarId
                || firstRawItem?.calendar_id
                || firstRawItem?.calendarId
                || null;
            return { calendarId, items };
        }
        catch (error) {
            return { calendarId: null, items: [] };
        }
    },
    async getServiceCalendar(serviceId) {
        const response = await calendarApi.getServiceCalendar(serviceId);
        return response.data || { availability: [] };
    },
    /**
     * Get time slots for a specific date
     * @param view - 'booked', 'available', or 'detailed' (default for calendar view with booking info)
     */
    async getSlotsByDate(serviceId, date, view = 'detailed') {
        const response = await calendarApi.getSlotsByDate(serviceId, date, view);
        return response.data || { slots: [], available: false, message: '', service_id: serviceId, date, timezone: '', schedule_type: 'regular', service_duration_minutes: 0 };
    },
    /**
     * Get schedule for a specific date
     */
    async getScheduleByDate(serviceId, date) {
        const response = await calendarApi.getScheduleByDate(serviceId, date);
        return response.data;
    },
    /**
     * Check availability for a potential booking (pre-booking validation)
     */
    async checkAvailability(serviceId, date, time, participants) {
        const response = await calendarApi.checkAvailability(serviceId, { date, time, participants });
        return response.data;
    },
    /**
     * Get availability data for a date range (up to 90 days)
     * Returns a map of dates with availability status and slot details
     */
    async getAvailabilityRange(serviceId, from, to) {
        const response = await calendarApi.getAvailabilityRange(serviceId, { from, to });
        return response.data;
    },
    /**
     * Get all availability rules for a service
     */
    async getAvailabilityRules(serviceId) {
        const response = await calendarApi.getAvailabilityRules(serviceId);
        return response.data || [];
    },
    async getOutOfServiceBookings(serviceId) {
        const response = await calendarApi.getOutOfServiceBookings(serviceId);
        return response.data || { service_id: serviceId, impacted_count: 0, bookings: [] };
    },
    /**
     * Upsert (replace all) availability rules
     */
    async upsertAvailability(serviceId, rules) {
        const response = await calendarApi.upsertAvailability(serviceId, rules);
        return response.data;
    },
    /**
     * Create override for a service
     */
    async createOverride(serviceId, data) {
        await calendarApi.createOverride(serviceId, data);
    },
    /**
     * Update availability rule
     */
    async updateAvailability(serviceId, availabilityId, data) {
        await calendarApi.updateAvailability(serviceId, availabilityId, data);
    },
    /**
     * Update override
     */
    async updateOverride(serviceId, overrideId, data) {
        await calendarApi.updateOverride(serviceId, overrideId, data);
    },
    /**
     * Delete availability rule
     */
    async deleteAvailability(serviceId, availabilityId) {
        await calendarApi.deleteAvailability(serviceId, availabilityId);
    },
    /**
     * Delete override
     */
    async deleteOverride(serviceId, overrideId) {
        await calendarApi.deleteOverride(serviceId, overrideId);
    },
};
/**
 * Process deleted items - now uses hard delete via service-centric endpoints
 */
export async function processDeletedItems(serviceId, deletedItems) {
    for (const deleted of deletedItems) {
        if (deleted.type === 'availability') {
            await calendarRepository.deleteAvailability(serviceId, deleted.id);
        }
        else if (deleted.type === 'override') {
            await calendarRepository.deleteOverride(serviceId, deleted.id);
        }
        else {
            throw new Error(`Unsupported deleted item type: ${deleted.type}`);
        }
    }
}
export function cleanAvailabilityData(item) {
    const { type, isExisting, isNew, isModified, id, ...cleanData } = item;
    return cleanData;
}
/**
 * Clean override data for API create/update.
 * The CreateServiceCalendarAvailabilityOverrideDto expects specific fields only.
 * Removes tracking flags and fields the API doesn't accept (tax_rate, date, updated_by).
 */
export function cleanOverrideForApi(item) {
    const data = item;
    const netPrice = numberOrDefault(data, ['net_price', 'netPrice', 'base_price', 'basePrice']);
    const totalPrice = calculateTotalPrice(data, netPrice);
    return {
        // Date range
        overrideStartingFrom: data.overrideStartingFrom,
        overrideUntil: data.overrideUntil,
        // Time range
        start_time: data.start_time?.toString() || '',
        end_time: data.end_time?.toString() || '',
        // Pricing - API expects base_price, net_price, total_price
        base_price: netPrice,
        net_price: netPrice,
        total_price: totalPrice,
        price_type: data.price_type || 'PERSON',
        commission_amount: numberOrDefault(data, ['commission_amount', 'commissionAmount']),
        commission_type: data.commission_type || 'PERCENTAGE',
        // Capacity
        standard_max_participants: numberOrDefault(data, ['standard_max_participants', 'standardMaxParticipants'], 1),
        overcapacity_max_participants: nullableNumber(data, 'overcapacity_max_participants', 'overcapacityMaxParticipants'),
        overflow_price: nullableNumber(data, 'overflow_price', 'overflowPrice'),
        // Options
        allow_reservations: data.allow_reservations !== false,
    };
}
export default calendarRepository;
